import { Box, Button, IconButton, Menu, MenuItem, Stack, Tooltip, Typography } from '@mui/material';
import DarkModeOutlinedIcon from '@mui/icons-material/DarkModeOutlined';
import LightModeOutlinedIcon from '@mui/icons-material/LightModeOutlined';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import { useState, type PropsWithChildren, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Logo } from './Logo';
import { UruannaMark } from '@/components/brand/UruannaMark';
import { LOCALES, LOCALE_LABELS } from '@/i18n/locales';
import { usePreferencesStore } from '@/store/preferences';
import { aqua, lavender, palettes } from '@/theme/tokens';

/**
 * The shell every screen before the console shares: sign in, register, verify,
 * reset, accept an invitation, and the registration wizard.
 *
 * It exists because those were two products. The auth pages were a dark split
 * with a 400px column; the wizard was a white topbar over the ground with a
 * 240px stepper beside a panel. One shell, one place the brand lives, and the
 * person who registers and then applies is walking through one building.
 *
 * Split screen (SignIn board): the brand on indigo (`brandPane`), the work on
 * white — and at night the brand on the panel grey, the work on the black
 * ground, so no indigo block survives into dark mode. The compact mark is
 * repeated large and faint behind the copy; it is the only thing on these
 * pages that moves (`UruannaMark animate="drift"`: one bloom into the
 * assembled mark, then the whole mark turns slowly as one), and it stands still under `prefers-reduced-motion`. The
 * words are static.
 *
 * The pane's own colours (white and white-alpha over indigo) are named here and
 * in the wizard's stepper and nowhere else.
 */
export interface AuthShellProps {
  /** `narrow` is a sign-in form; `wide` is the wizard, which holds cards. */
  width?: 'narrow' | 'wide';
  /**
   * Shown in the brand pane under the headline, in place of the three points.
   * The wizard puts the rungs there, which turns the decorative half into
   * working chrome and lets the work column keep the whole of its width.
   */
  aside?: ReactNode;
  /** One more quiet control in the top row of the work column (sign out). */
  topRight?: ReactNode;
  /** Shown above the work on widths where the pane is not drawn (the wizard's stepper). */
  mobileAside?: ReactNode;
}

const WIDTHS = { narrow: 400, wide: 620 } as const;

/** Over the indigo pane. */
const ON_PANE = {
  strong: palettes.light.onPrimary,
  body: lavender[100],
  muted: 'rgba(255,255,255,0.72)',
} as const;

export function AuthShell({
  width = 'narrow',
  aside,
  topRight,
  mobileAside,
  children,
}: PropsWithChildren<AuthShellProps>) {
  const { t } = useTranslation();

  return (
    <Box
      data-auth-shell=""
      sx={{
        minHeight: '100dvh',
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
        /*
         * The sign-in board's sizes for these pages only: a 44px input and a
         * 46px primary act. Both grow the box symmetrically from the theme's
         * geometry (a 22px line in 11px of padding instead of 10), so the value
         * stays centred exactly as the theme centres it everywhere else.
         */
        '& .MuiOutlinedInput-root:not(.MuiInputBase-sizeSmall):not(.MuiInputBase-multiline) .MuiOutlinedInput-input':
          { paddingBlock: '11px' },
        '& .MuiButton-sizeLarge': { minHeight: 46, fontSize: '1rem' },
      }}
    >
      <Box
        sx={{
          display: { xs: 'none', md: 'block' },
          position: 'relative',
          // `clip`, not `hidden`: hidden would make the pane a scroll container
          // and the sticky column inside it would never stick.
          overflow: 'clip',
          bgcolor: 'brand.brandPane',
          color: ON_PANE.strong,
        }}
      >
        {/* Sticky, so a long wizard step scrolls past a pane that stays put. */}
        <Box
          sx={{
            position: 'sticky',
            top: 0,
            height: '100dvh',
            boxSizing: 'border-box',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            gap: 6,
            p: 7,
            overflow: 'hidden',
          }}
        >
          <Box
            aria-hidden
            data-testid="pane-mark"
            sx={{
              position: 'absolute',
              insetInlineEnd: -150,
              bottom: -110,
              opacity: 0.09,
              pointerEvents: 'none',
            }}
          >
            <UruannaMark size={645} tone="inverse" animate="drift" decorative />
          </Box>

          <Box sx={{ position: 'relative' }}>
            <Logo tone="inverse" variant="lockup" />
          </Box>

          <Stack spacing={2.25} sx={{ position: 'relative', maxWidth: 520 }}>
            <Typography
              component="p"
              sx={{
                m: 0,
                color: ON_PANE.strong,
                fontSize: '2.25rem',
                fontWeight: 600,
                lineHeight: 1.4,
                textWrap: 'balance',
              }}
            >
              {t('auth.pane.headline')}
            </Typography>
            {aside ? (
              <Box sx={{ pt: 2 }}>{aside}</Box>
            ) : (
              <>
                <Typography sx={{ color: ON_PANE.body, fontSize: '1.0625rem', lineHeight: 1.8 }}>
                  {t('auth.pane.body')}
                </Typography>
                <Stack component="ul" spacing={1.25} sx={{ listStyle: 'none', m: 0, mt: 1, p: 0 }}>
                  {(['verified', 'prescriptions', 'deliveries'] as const).map((point) => (
                    <Stack
                      component="li"
                      key={point}
                      direction="row"
                      spacing={1.25}
                      alignItems="center"
                      sx={{ fontSize: '0.9375rem', color: ON_PANE.strong }}
                    >
                      <Box
                        aria-hidden
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '2px',
                          bgcolor: aqua.signal,
                          flexShrink: 0,
                        }}
                      />
                      <span>{t(`auth.pane.points.${point}`)}</span>
                    </Stack>
                  ))}
                </Stack>
              </>
            )}
          </Stack>

          <Typography sx={{ position: 'relative', color: ON_PANE.muted, fontSize: '0.8125rem' }}>
            {t('app.copyright')}
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          px: { xs: 2.5, sm: 7 },
          pt: { xs: 2.5, sm: 4 },
          pb: { xs: 4, sm: 7 },
          bgcolor: (theme) =>
            theme.palette.mode === 'light' ? 'background.paper' : 'background.default',
        }}
      >
        {/*
         * The language and the colour scheme sit above the work, not under it.
         * This product opens in Arabic by default, so someone who wants
         * English wants it before reading the form.
         */}
        <Stack direction="row" alignItems="center" spacing={1} sx={{ minHeight: 36 }}>
          <Box sx={{ display: { md: 'none' } }}>
            <Logo height={30} />
          </Box>
          <Box sx={{ flex: 1 }} />
          {topRight}
          <LanguageButton />
          <ThemeButton />
        </Stack>

        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            width: '100%',
            maxWidth: WIDTHS[width],
            mx: 'auto',
            py: { xs: 3, sm: 5 },
          }}
        >
          {mobileAside ? <Box sx={{ display: { md: 'none' }, mb: 3 }}>{mobileAside}</Box> : null}
          {children}
        </Box>

        <Typography
          sx={{
            display: { md: 'none' },
            textAlign: 'center',
            color: 'text.secondary',
            fontSize: '0.75rem',
          }}
        >
          {t('app.copyright')}
        </Typography>
      </Box>
    </Box>
  );
}

/** The three languages behind one 36px control, the chosen one ticked. */
function LanguageButton() {
  const { t, i18n } = useTranslation();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const current = LOCALES.find((locale) => i18n.language.startsWith(locale)) ?? 'ar';

  return (
    <>
      <Button
        variant="outlined"
        color="inherit"
        size="small"
        aria-haspopup="menu"
        aria-expanded={Boolean(anchor)}
        aria-label={`${t('auth.language')}: ${LOCALE_LABELS[current]}`}
        endIcon={<KeyboardArrowDownRoundedIcon sx={{ fontSize: 18 }} />}
        onClick={(event) => setAnchor(event.currentTarget)}
        sx={{ height: 36, px: 1.5, borderColor: 'divider', fontWeight: 500 }}
      >
        <Box component="span" lang={current}>
          {LOCALE_LABELS[current]}
        </Box>
      </Button>
      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}>
        {LOCALES.map((locale) => (
          <MenuItem
            key={locale}
            lang={locale}
            selected={locale === current}
            onClick={() => {
              setAnchor(null);
              void i18n.changeLanguage(locale);
            }}
            sx={{ minWidth: 160, gap: 1.5, justifyContent: 'space-between' }}
          >
            {LOCALE_LABELS[locale]}
            {locale === current ? (
              <CheckRoundedIcon sx={{ fontSize: 18, color: 'primary.main' }} />
            ) : null}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}

/** Light or dark, from the same preference the console's settings write. */
function ThemeButton() {
  const { t } = useTranslation();
  const setMode = usePreferencesStore((s) => s.setMode);
  const resolved = usePreferencesStore((s) => s.resolvedMode());
  const next = resolved === 'dark' ? 'light' : 'dark';
  const label = next === 'dark' ? t('auth.darkMode') : t('auth.lightMode');

  return (
    <Tooltip title={label}>
      <IconButton
        aria-label={label}
        onClick={() => setMode(next)}
        sx={{ width: 36, height: 36, border: 1, borderColor: 'divider', color: 'text.secondary' }}
      >
        {next === 'dark' ? (
          <DarkModeOutlinedIcon sx={{ fontSize: 18 }} />
        ) : (
          <LightModeOutlinedIcon sx={{ fontSize: 18 }} />
        )}
      </IconButton>
    </Tooltip>
  );
}
